import api from './client';
import type { Job, Quote, QuoteLineItem, QuoteGroup, Client, LabourRole, TransportOption, OtherOption, QuoteSubHireDetail } from '@/types';

export const jobsApi = {
  list: () => api.get<Job[]>('/jobs').then((r) => r.data),
  search: (q: string) => api.get<Job[]>('/jobs', { params: { search: q } }).then((r) => r.data),
  get:  (id: string) => api.get<Job>(`/jobs/${id}`).then((r) => r.data),
  create: (data: Partial<Job>) => api.post<Job>('/jobs', data).then((r) => r.data),
  update: (id: string, data: Partial<Job>) => api.patch<Job>(`/jobs/${id}`, data).then((r) => r.data),
  cancel: (id: string) => api.delete(`/jobs/${id}`).then((r) => r.data),
  hardDelete: (id: string) => api.delete(`/jobs/${id}`, { params: { permanent: 'true' } }).then((r) => r.data),
};

export const quotesApi = {
  get:    (id: string) => api.get<Quote>(`/quotes/${id}`).then((r) => r.data),
  update: (id: string, data: Partial<Quote>) => api.patch<Quote>(`/quotes/${id}`, data).then((r) => r.data),
  setStatus: (id: string, status: Quote['status']) =>
    api.post<Quote>(`/quotes/${id}/status`, { status }).then((r) => r.data),

  // Line items
  addLine:    (quoteId: string, data: Partial<QuoteLineItem>) =>
    api.post<QuoteLineItem>(`/quotes/${quoteId}/lines`, data).then((r) => r.data),
  updateLine: (quoteId: string, lineId: string, data: Partial<QuoteLineItem>) =>
    api.patch<QuoteLineItem>(`/quotes/${quoteId}/lines/${lineId}`, data).then((r) => r.data),
  deleteLine: (quoteId: string, lineId: string) =>
    api.delete(`/quotes/${quoteId}/lines/${lineId}`).then((r) => r.data),

  // Groups
  addGroup:    (quoteId: string, data: Partial<QuoteGroup>) =>
    api.post<QuoteGroup>(`/quotes/${quoteId}/groups`, data).then((r) => r.data),
  updateGroup: (quoteId: string, groupId: string, data: Partial<QuoteGroup>) =>
    api.patch<QuoteGroup>(`/quotes/${quoteId}/groups/${groupId}`, data).then((r) => r.data),
  deleteGroup: (quoteId: string, groupId: string) =>
    api.delete(`/quotes/${quoteId}/groups/${groupId}`).then((r) => r.data),

  // Sub-hire per-supplier details (transport / dates / notes-to-supplier)
  upsertSubHireDetail: (quoteId: string, supplierId: string, data: Partial<QuoteSubHireDetail>) =>
    api.put<QuoteSubHireDetail>(`/quotes/${quoteId}/sub-hire-details/${supplierId}`, data).then((r) => r.data),
  deleteSubHireDetail: (quoteId: string, supplierId: string) =>
    api.delete(`/quotes/${quoteId}/sub-hire-details/${supplierId}`).then((r) => r.data),

  // PDFs
  downloadQuotePdf:     (quoteId: string) => `/api/pdfs/quote/${quoteId}`,
  downloadPullSheetPdf: (quoteId: string) => `/api/pdfs/pullsheet/${quoteId}`,
  downloadPurchaseOrderPdf: (quoteId: string, supplier: string) =>
    `/api/pdfs/purchase-order/${quoteId}?supplier=${encodeURIComponent(supplier)}`,
};

export const clientsApi = {
  list:   () => api.get<Client[]>('/clients').then((r) => r.data),
  get:    (id: string) => api.get<Client>(`/clients/${id}`).then((r) => r.data),
  create: (data: Partial<Client>) => api.post<Client>('/clients', data).then((r) => r.data),
  update: (id: string, data: Partial<Client>) => api.patch<Client>(`/clients/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/clients/${id}`).then((r) => r.data),
};

export const catalogueApi = {
  listLabour:   () => api.get<LabourRole[]>('/catalogue/labour').then((r) => r.data),
  createLabour: (data: Partial<LabourRole>) => api.post<LabourRole>('/catalogue/labour', data).then((r) => r.data),
  updateLabour: (id: string, data: Partial<LabourRole>) => api.patch<LabourRole>(`/catalogue/labour/${id}`, data).then((r) => r.data),
  deleteLabour: (id: string) => api.delete(`/catalogue/labour/${id}`).then((r) => r.data),

  listTransport:   () => api.get<TransportOption[]>('/catalogue/transport').then((r) => r.data),
  createTransport: (data: Partial<TransportOption>) => api.post<TransportOption>('/catalogue/transport', data).then((r) => r.data),
  updateTransport: (id: string, data: Partial<TransportOption>) =>
    api.patch<TransportOption>(`/catalogue/transport/${id}`, data).then((r) => r.data),
  deleteTransport: (id: string) => api.delete(`/catalogue/transport/${id}`).then((r) => r.data),

  listOther:   () => api.get<OtherOption[]>('/catalogue/other').then((r) => r.data),
  createOther: (data: Partial<OtherOption>) => api.post<OtherOption>('/catalogue/other', data).then((r) => r.data),
  updateOther: (id: string, data: Partial<OtherOption>) =>
    api.patch<OtherOption>(`/catalogue/other/${id}`, data).then((r) => r.data),
  deleteOther: (id: string) => api.delete(`/catalogue/other/${id}`).then((r) => r.data),
};

export const dashboardApi = {
  get: () => api.get('/dashboard').then((r) => r.data),
  calendar: (year: number, month: number) =>
    api.get('/dashboard/calendar', { params: { year, month } }).then((r) => r.data),
};
